//@flow

import moment from 'moment';
import isEmpty from 'lodash/isEmpty';

import cookies from '../../lib/cookies';
import { ServicesLodgeDetails as Icons } from '../../constants/icons';
import { removeSpaces } from '../../lib/utils';
import { padSAtTheEndIfPlural } from '../../lib/helpers';

// ----------------------------------------
// SETTING AND GETTING FROM COOKIES
// ----------------------------------------

const lodgePax = 'LODGE_PAX';
const lodgeCheckinDate = 'LODGE_CHECKIN_DATE';
const lodgeCheckoutDate = 'LODGE_CHECKOUT_DATE';
const queryNameResult = 'LODGE_QUERY_RESULT';
const FIFTEEN_MINUTES = 900000;

const setCookie15Mins = (name: string, val: any) =>
  cookies.prototype.setCookie(name, val, null, FIFTEEN_MINUTES);

type Values = {
  checkinDate: string,
  checkoutDate: string,
  pax: string,
  queryName?: Object | null,
};

export function setCookies15Mins(values: Values) {
  const { checkinDate, checkoutDate, pax, queryName } = values;

  setCookie15Mins(lodgeCheckinDate, checkinDate);
  setCookie15Mins(lodgeCheckoutDate, checkoutDate);
  setCookie15Mins(lodgePax, pax);

  if (!isEmpty(queryName)) setCookie15Mins(queryNameResult, JSON.stringify(queryName));
}

type Args = {
  pax: number,
  checkinDate: string,
  checkoutDate: string,
  queryName: Object | null,
};

const getFromCookies = (): Args => ({
  pax: Number(cookies.prototype.getCookie(lodgePax)) || 2,
  checkinDate: cookies.prototype.getCookie(lodgeCheckinDate) || '',
  checkoutDate: cookies.prototype.getCookie(lodgeCheckoutDate) || '',
  queryName: JSON.parse(cookies.prototype.getCookie(queryNameResult) || null),
});

// ----------------------------------------

function getDiffInDays(startDate: string | null, endDate: string | null) {
  if (startDate && endDate) {
    const diffInDays = Number(moment(endDate).diff(moment(startDate), 'd'));

    return diffInDays <= 0 ? 0 : diffInDays;
  }

  return 1;
}

// ----------------------------------------

function constructLabel(placesOrLodgesResults: any) {
  const { documentType, name, region, address } = placesOrLodgesResults;

  let label = '';
  const regionName = `${region} Region`;
  if (documentType === 'locality') {
    label = `${name}, ${regionName}`;
  }
  if (documentType === 'lodge') {
    label = `${name}, ${address}, ${regionName}`;
  }

  return label;
}

const constructTitle = ({
  name,
  address,
  region,
}: {
  name: null | string,
  address: null | string,
  region: null | string,
}) => {
  name = name ? `${name}, ` : '';
  address = address ? `${address}, ` : '';
  region = region ? `${region} Region` : '';

  return `${name}${address}${region}`;
};

const provideIcon = (serviceName: string): string => {
  serviceName = removeSpaces(serviceName).toLowerCase();
  return Icons[serviceName] ? Icons[serviceName] : Icons.default;
};

const showPriceAndRooms = (numberOfDays: number, rooms: number): string =>
  `Price for ${numberOfDays} ${padSAtTheEndIfPlural(
    numberOfDays,
    'Night'
  )} and ${rooms} ${padSAtTheEndIfPlural(rooms, 'Room')}`;

type Money = null | {
  amount: string,
  currency: string,
};

type Lodge = {
  id: string,
  name: string,
  bannerImages: {
    large: string | null,
    medium: string | null,
    small: string | null,
    tiny: string | null,
  },
  place: { id: string },
  address: string | null,
  altitude: string | null,
  primary_image: string | null,
  rooms: Array<?{
    title: string,
    roomType: string,
    numberOfRooms: number,
    roomClass: string,
    numBedsPerClass: number,
  }>,
  services: Array<?{
    name: string,
    note: string,
    rate: {
      amount: string,
      currency: string,
    },
  }>,
  phoneNumbers: Array<?string>,
  includedServices: { [string]: Array<?string> },
  mealPlan: { [string]: Money },
  currency: string,
  startingPrice: Money,
  paymentPlan: {
    refundable: number,
    nonrefundable: number,
  },
  region: string | null,
  is_bookable: boolean,
};

export {
  constructTitle,
  provideIcon,
  getFromCookies,
  constructLabel,
  getDiffInDays,
  showPriceAndRooms,
};
export type { Lodge };



// WEBPACK FOOTER //
// ./src/screens/Lodge/helpers.js